/**
 * Helpers for the Client Engagement card. These are intentionally tiny — they
 * deal exclusively with *displaying* values the backend already produced
 * (no client-side scoring).
 */

const TIME_UNIT_LABELS: Record<string, { singular: string; plural: string }> = {
    d: { singular: 'day', plural: 'days' },
    w: { singular: 'week', plural: 'weeks' },
    m: { singular: 'month', plural: 'months' },
    y: { singular: 'year', plural: 'years' },
};

const TIME_SUFFIX_RE = /^(\d+)([dwmy])$/i;

/**
 * Convert a snake_case rule name into a readable label, expanding short time
 * windows like `30d` → "last 30 days".
 *
 * Examples:
 *   successful_logins_30d   → "Successful logins, last 30 days"
 *   document_uploads_30d    → "Document uploads, last 30 days"
 *   failed_logins_7d        → "Failed logins, last 7 days"
 *   stale_case_no_recent_activity → "Stale case no recent activity"
 */
export const humanizeRuleName = (rule: string): string => {
    if (!rule) return '';
    const parts = rule.split('_').filter(Boolean);
    if (parts.length === 0) return rule;

    const last = parts[parts.length - 1];
    const timeMatch = TIME_SUFFIX_RE.exec(last);

    const baseWords = timeMatch ? parts.slice(0, -1) : parts;
    const sentence = baseWords.join(' ').toLowerCase();
    const capitalized = sentence.charAt(0).toUpperCase() + sentence.slice(1);

    if (!timeMatch) return capitalized;

    const amount = parseInt(timeMatch[1], 10);
    const unit = TIME_UNIT_LABELS[timeMatch[2].toLowerCase()];
    const unitLabel = unit ? (amount === 1 ? unit.singular : unit.plural) : timeMatch[2];

    return `${capitalized}, last ${amount} ${unitLabel}`;
};

/**
 * Classify a score-breakdown value (the raw string returned by the backend,
 * e.g. "+12", "-3", "0") as positive / negative / neutral so the value chip
 * can be styled appropriately. We deliberately do not parse or mutate the
 * value — the raw string is still what gets displayed.
 */
export const getValueTone = (value: string): 'positive' | 'negative' | 'neutral' => {
    if (!value) return 'neutral';
    const trimmed = value.trim();
    if (trimmed.startsWith('-')) return 'negative';
    if (trimmed.startsWith('+')) return 'positive';
    const asNumber = Number(trimmed);
    if (!Number.isNaN(asNumber)) {
        if (asNumber > 0) return 'positive';
        if (asNumber < 0) return 'negative';
    }
    return 'neutral';
};
